import type { RequestOptions } from '../services/HttpClient';
import { createApiError } from '../utils/errors';

// Lazy import to avoid circular dependency
// Cache the import promise to avoid multiple imports
let httpClientPromise: Promise<
  (typeof import('../services/HttpClient'))['httpClient']
> | null = null;

function getHttpClient() {
  if (!httpClientPromise) {
    httpClientPromise = import('../services/HttpClient').then(
      module => module.httpClient
    );
  }
  return httpClientPromise;
}

export abstract class BaseService {
  protected async request<T = unknown>(
    url: string,
    options?: RequestOptions & { useUserId?: boolean; userId?: string }
  ): Promise<T> {
    const httpClient = await getHttpClient();
    const response = await httpClient.request<T>(url, options);
    // Check if we have an error response
    if (response.error) {
      throw createApiError(500, response.error);
    }
    // For successful responses, return the data (even if undefined for 204 responses)
    return response.data as T;
  }

  protected async get<T = unknown>(
    url: string,
    options?: RequestOptions & { useUserId?: boolean; userId?: string }
  ): Promise<T> {
    return this.request<T>(url, { ...options, method: 'GET' });
  }

  protected async post<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions & { useUserId?: boolean; userId?: string }
  ): Promise<T> {
    const isFormData = data instanceof FormData;

    return this.request<T>(url, {
      ...options,
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      // For FormData, set Content-Type to undefined to let browser set it with boundary
      headers: isFormData
        ? ({
            ...options?.headers,
            'Content-Type': undefined,
          } as HeadersInit)
        : options?.headers,
    });
  }

  protected async put<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions & { useUserId?: boolean; userId?: string }
  ): Promise<T> {
    const isFormData = data instanceof FormData;

    return this.request<T>(url, {
      ...options,
      method: 'PUT',
      body: isFormData ? data : JSON.stringify(data),
      // For FormData, set Content-Type to undefined to let browser set it with boundary
      headers: isFormData
        ? ({
            ...options?.headers,
            'Content-Type': undefined,
          } as HeadersInit)
        : options?.headers,
    });
  }

  protected async patch<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    const isFormData = data instanceof FormData;

    return this.request<T>(url, {
      ...options,
      method: 'PATCH',
      body: isFormData ? data : JSON.stringify(data),
      // For FormData, set Content-Type to undefined to let browser set it with boundary
      headers: isFormData
        ? ({
            ...options?.headers,
            'Content-Type': undefined,
          } as HeadersInit)
        : options?.headers,
    });
  }

  protected async delete<T = unknown>(
    url: string,
    data?: unknown,
    options?: RequestOptions & { useUserId?: boolean; userId?: string }
  ): Promise<T> {
    const isFormData = data instanceof FormData;

    return this.request<T>(url, {
      ...options,
      method: 'DELETE',
      body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
      // For FormData, set Content-Type to undefined to let browser set it with boundary
      headers: isFormData
        ? ({
            ...options?.headers,
            'Content-Type': undefined,
          } as HeadersInit)
        : options?.headers,
    });
  }
}
